/**
 * HospitalMap — South Tyrol province silhouette with 7 beating ER markers.
 *
 * Layout: eyebrow "SULLA MAPPA" + Newsreader h2 + lede, then the map card.
 * Placed between the oggi-vs-tipico hero and the hospital cards grid.
 *
 * Rendering:
 *   - Province silhouette via @visx/geo <Mercator fitExtent> — local GeoJSON
 *     from /geo/south-tyrol.geojson (no external tile requests, ever).
 *   - 7 markers projected from each hospital's lat/lon.
 *   - Beats reuse the existing `beat`/`ring` keyframes + lib/busyness.ts.
 *   - Pulse rate encodes busyness (busier = faster beat), seeded phase offsets
 *     so neighbours don't lock-step.
 *
 * Congestion → colour mapping (brand-space, triage QUARANTINED):
 *   calm     → --slate   (neutral)
 *   moderate → --violet
 *   busy     → --pulse   (rose)
 *   unknown  → transparent fill + dashed --muted ring, no beat
 *
 * States:
 *   Silhouette renders IMMEDIATELY — static geometry, no data dep.
 *   Loading  → silhouette only; no markers until hospitals array arrives.
 *   Error    → all 7 markers dashed unknown + quiet inline error + Riprova.
 *   Per-hospital no-data → dashed unknown, no beat.
 *
 * A11y:
 *   SVG role="group" + <title>/<desc> (aria-labelledby) — a group, not an img,
 *   because it CONTAINS the focusable link markers (role="img" may not have
 *   focusable descendants → axe nested-interactive).
 *   Province path aria-hidden.
 *   Each marker role="link", tabindex=0, focus-order = calmest-first.
 *   Enter/Space activates navigation to /ps/:slug.
 *   Visible focus ring (--pulse at r+6), distinct from hover (scale 1.12×).
 *   .sr-only <table> fallback lists all 7 rows for screen readers.
 *
 * prefers-reduced-motion: markers static (no beat/ring), full colour kept.
 */

import { useEffect, useState, useCallback, useRef, useId, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Mercator } from "@visx/geo";
import { ParentSize } from "@visx/responsive";
import type { FeatureCollection, Polygon, GeoJsonProperties } from "geojson";
import type { HospitalQueueState } from "@/api/generated/model";
import { congestionLevel, CONGESTION_LABEL } from "@/lib/congestion";
import { beatDuration, beatDurationStyle, beatPhaseStyle } from "@/lib/busyness";
import { ensureCCW, focusOrderedHospitals, markerVariant, projectCoord } from "@/lib/map";
import type { PointProjection, ProvinceFeature } from "@/lib/map";

// ── Constants ─────────────────────────────────────────────────────────────────

const GEO_URL = "/geo/south-tyrol.geojson";

/** Core marker radius. */
const CORE_R = 8;
/** Focus ring radius offset from core. */
const FOCUS_RING_R = CORE_R + 6;
/** Map container height in px. */
const MAP_HEIGHT = 320;

// ── i18n strings ──────────────────────────────────────────────────────────────

const STRINGS = {
  mapEyebrow: "SULLA MAPPA",
  mapH2Start: "Dove respira più ",
  mapH2Emphasis: "tranquillo",
  mapH2End: ", adesso",
  mapLede:
    "Ogni punto è un pronto soccorso: il colore indica quanto è affollato, il ritmo batte con la coda.",
  mapTitleAccessible:
    "Mappa dei pronto soccorso dell'Alto Adige con indicatori di affluenza in tempo reale",
  mapDescAccessible:
    "Sette punti sulla mappa della provincia di Bolzano mostrano l'affollamento attuale di ogni pronto soccorso. Il colore e il ritmo di ogni punto indicano il livello: grigio per tranquillo, viola per movimentato, rosso-rosa per affollato. I punti tratteggiati indicano assenza di dati.",
  srTableCaption: "Pronto soccorso dell'Alto Adige — stato attuale",
  srColName: "Pronto Soccorso",
  srColNameDe: "Bezeichnung (DE)",
  srColWaiting: "In attesa",
  srColStatus: "Stato",
  srNoData: "Nessun dato",
  error: "Dati momentaneamente non disponibili.",
  retry: "Riprova",
} as const;

// ── Types ──────────────────────────────────────────────────────────────────────

// ProvinceFeature is imported from @/lib/map (single source of truth).
type ProvinceGeoJSON = FeatureCollection<Polygon, GeoJsonProperties>;

export interface HospitalMapProps {
  hospitals: HospitalQueueState[] | undefined;
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
}

// ── Marker component ──────────────────────────────────────────────────────────

interface MarkerProps {
  hospital: HospitalQueueState;
  x: number;
  y: number;
  isPending: boolean;
  isError: boolean;
  titleId: string;
}

function HospitalMarker({ hospital, x, y, isPending, isError, titleId }: MarkerProps) {
  const navigate = useNavigate();
  const level = isError || isPending ? "unknown" : congestionLevel(hospital);
  const variant = markerVariant(level);

  // Only known-data, non-error, non-pending markers beat
  const canBeat = !isPending && !isError && hospital.hasData && level !== "unknown";
  const beatDur = canBeat ? beatDuration(hospital.totalWaiting, level) : 1.7;

  const handleClick = useCallback(() => {
    void navigate(`/ps/${hospital.slug}`);
  }, [navigate, hospital.slug]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        void navigate(`/ps/${hospital.slug}`);
      }
    },
    [navigate, hospital.slug],
  );

  return (
    <g
      role="link"
      tabIndex={0}
      aria-labelledby={titleId}
      className={`map-marker map-marker--${variant}${canBeat ? " map-marker--beating" : ""}`}
      style={{
        cursor: "pointer",
        ...beatDurationStyle(beatDur),
        ...beatPhaseStyle(hospital.slug),
      }}
      transform={`translate(${x},${y})`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {/* Invisible touch-safe hit target (~22px diameter) */}
      <circle r={11} fill="transparent" />

      {/* Expanding live ring (only for beating markers) */}
      {canBeat && (
        <circle r={CORE_R} className="map-ring" fill="none" strokeWidth={1.5} aria-hidden="true" />
      )}

      {/* Core dot */}
      <circle r={CORE_R} className="map-core" aria-hidden="true" />

      {/* Focus ring — visible only with :focus-visible via CSS */}
      <circle
        r={FOCUS_RING_R}
        className="map-focus-ring"
        fill="none"
        strokeWidth={2}
        aria-hidden="true"
      />

      {/* Accessible title (referenced by aria-labelledby on the <g>) */}
      <title id={titleId}>
        {hospital.nameIt} — {hospital.nameDe}
        {hospital.hasData
          ? `: ${hospital.totalWaiting} persone in attesa, ${CONGESTION_LABEL[level]}`
          : `: ${STRINGS.srNoData}`}
      </title>
    </g>
  );
}

// ── Tooltip ───────────────────────────────────────────────────────────────────

interface TooltipData {
  hospital: HospitalQueueState;
  x: number;
  y: number;
}

function MapTooltip({
  data,
  mapWidth,
  mapHeight,
  isPending,
  isError,
}: {
  data: TooltipData;
  mapWidth: number;
  mapHeight: number;
  isPending: boolean;
  isError: boolean;
}) {
  const { hospital, x, y } = data;
  // Mirror HospitalMarker:108 — force unknown in error/pending so the tooltip
  // never shows a stale real level while markers display as dashed-unknown.
  const level = isError || isPending ? "unknown" : congestionLevel(hospital);
  const label = CONGESTION_LABEL[level];

  // Flip tooltip left when the marker is in the right 30% of the map
  const tooltipLeft = x > mapWidth * 0.7 ? undefined : x + CORE_R + 8;
  const tooltipRight = x > mapWidth * 0.7 ? mapWidth - x + CORE_R + 8 : undefined;
  const tooltipTop = Math.max(4, Math.min(y - 24, mapHeight - 80));

  return (
    <div
      className="map-tooltip"
      role="tooltip"
      style={{
        position: "absolute",
        left: tooltipLeft !== undefined ? tooltipLeft : "auto",
        right: tooltipRight !== undefined ? tooltipRight : "auto",
        top: tooltipTop,
        pointerEvents: "none",
      }}
    >
      <p className="map-tooltip__name serif">{hospital.nameIt}</p>
      <p className="map-tooltip__name-de">{hospital.nameDe}</p>
      <p className="map-tooltip__waiting tnum">
        {hospital.hasData ? `${hospital.totalWaiting} in attesa` : STRINGS.srNoData}
      </p>
      <span className={`level-chip level-chip--${level}`}>{label}</span>
    </div>
  );
}

// ── Screen-reader fallback table ─────────────────────────────────────────────

function SrTable({
  hospitals,
  isPending,
  isError,
}: {
  hospitals: HospitalQueueState[];
  isPending: boolean;
  isError: boolean;
}) {
  const ordered = focusOrderedHospitals(hospitals);
  return (
    // Visually-hidden accessible companion to the map. The wrapper does the
    // hiding/clipping: a `.sr-only` table can't shrink below its caption's
    // min-content width, so hiding the table directly would push the page into
    // horizontal scroll on mobile (issue #45).
    <div className="sr-only">
      <table>
        <caption>{STRINGS.srTableCaption}</caption>
        <thead>
          <tr>
            <th scope="col">{STRINGS.srColName}</th>
            <th scope="col">{STRINGS.srColNameDe}</th>
            <th scope="col">{STRINGS.srColWaiting}</th>
            <th scope="col">{STRINGS.srColStatus}</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((h) => {
            const level = isError || isPending ? "unknown" : congestionLevel(h);
            return (
              <tr key={h.slug}>
                <td>{h.nameIt}</td>
                <td>{h.nameDe}</td>
                <td>{h.hasData && !isError && !isPending ? String(h.totalWaiting) : "—"}</td>
                <td>{CONGESTION_LABEL[level]}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Map inner SVG (size-driven, receives resolved width) ─────────────────────
// Exported for direct unit tests at fixed size (same pattern as HistoryChart).

export interface MapInnerProps {
  feature: ProvinceFeature;
  hospitals: HospitalQueueState[];
  isPending: boolean;
  isError: boolean;
  width: number;
  height: number;
  svgTitleId: string;
  svgDescId: string;
}

export function MapInner({
  feature,
  hospitals,
  isPending,
  isError,
  width,
  height,
  svgTitleId,
  svgDescId,
}: MapInnerProps) {
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);

  const focusOrdered = focusOrderedHospitals(hospitals);

  // fitExtent inset so markers on the province edge aren't clipped
  const fitExtent: [[number, number], [number, number]] = [
    [16, 16],
    [width - 16, height - 16],
  ];

  return (
    <div style={{ position: "relative", width, height }}>
      <svg
        width={width}
        height={height}
        role="group"
        aria-labelledby={`${svgTitleId} ${svgDescId}`}
        style={{ display: "block", overflow: "visible" }}
      >
        <title id={svgTitleId}>{STRINGS.mapTitleAccessible}</title>
        <desc id={svgDescId}>{STRINGS.mapDescAccessible}</desc>

        <Mercator<ProvinceFeature> data={[feature]} fitExtent={[fitExtent, feature]}>
          {({ path, projection }) => {
            // Cast to our minimal PointProjection interface — d3-geo GeoProjection
            // satisfies this but TypeScript can't verify it without the full d3 types.
            const proj = projection as unknown as PointProjection;

            return (
              <g>
                {/* Province silhouette — decorative, aria-hidden */}
                <path d={path(feature) ?? ""} className="map-province" aria-hidden="true" />

                {/* Hospital markers in focus order (calmest-first) */}
                {focusOrdered.map((hospital) => {
                  if (hospital.latitude === null || hospital.longitude === null) return null;
                  const pt = projectCoord(proj, hospital.latitude, hospital.longitude);
                  if (!pt) return null;
                  const [mx, my] = pt;
                  const titleId = `map-marker-title-${hospital.slug}`;

                  return (
                    <g
                      key={hospital.slug}
                      onMouseEnter={() => setTooltipData({ hospital, x: mx, y: my })}
                      onMouseLeave={() => setTooltipData(null)}
                      onFocus={() => setTooltipData({ hospital, x: mx, y: my })}
                      onBlur={() => setTooltipData(null)}
                    >
                      <HospitalMarker
                        hospital={hospital}
                        x={mx}
                        y={my}
                        isPending={isPending}
                        isError={isError}
                        titleId={titleId}
                      />
                    </g>
                  );
                })}
              </g>
            );
          }}
        </Mercator>
      </svg>

      {/* Tooltip — suppressed during loading AND error (stale data guard) */}
      {tooltipData !== null && !isPending && !isError && (
        <MapTooltip
          data={tooltipData}
          mapWidth={width}
          mapHeight={height}
          isPending={isPending}
          isError={isError}
        />
      )}
    </div>
  );
}

// ── HospitalMapCard ───────────────────────────────────────────────────────────

function HospitalMapCard({
  feature,
  hospitals,
  isPending,
  isError,
  onRetry,
}: {
  feature: ProvinceFeature;
  hospitals: HospitalQueueState[];
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const svgTitleId = useId();
  const svgDescId = useId();

  // Last-good-width guard: cache the most recent non-zero width so a
  // transient degenerate report from ParentSize during scroll / forced
  // relayout renders at the last known good size rather than collapsing
  // all markers to the centre for one frame.
  const lastGoodWidth = useRef(0);

  return (
    <div className="map-card">
      {/* parentSizeStyles fixes the height-0 clip bug: ParentSize's inner
          measurement shim is position:absolute with top/right/bottom/left=0;
          when the outer container has no height the shim collapses to 0 and
          its overflow:hidden clips the SVG to nothing.  Giving the outer div
          an explicit height propagates to the shim and the SVG is visible. */}
      <ParentSize parentSizeStyles={{ height: MAP_HEIGHT }}>
        {({ width }) => {
          if (width > 0) lastGoodWidth.current = width;
          const renderWidth = lastGoodWidth.current;
          return renderWidth > 0 ? (
            <MapInner
              feature={feature}
              hospitals={hospitals}
              isPending={isPending}
              isError={isError}
              width={renderWidth}
              height={MAP_HEIGHT}
              svgTitleId={svgTitleId}
              svgDescId={svgDescId}
            />
          ) : null;
        }}
      </ParentSize>

      {/* Error overlay — silhouette stays, markers drop to unknown */}
      {isError && (
        <div className="map-error" role="alert">
          <span>{STRINGS.error}</span>
          <button type="button" className="overview__retry" onClick={onRetry}>
            {STRINGS.retry}
          </button>
        </div>
      )}

      {/* SR-only fallback table — screen readers get linearised data */}
      <SrTable hospitals={hospitals} isPending={isPending} isError={isError} />
    </div>
  );
}

// ── HospitalMap (public API — fetches GeoJSON, never blank on silhouette) ─────

export function HospitalMap({ hospitals, isPending, isError, onRetry }: HospitalMapProps) {
  const [geoFeature, setGeoFeature] = useState<ProvinceFeature | null>(null);
  const [geoError, setGeoError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(GEO_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`geo fetch ${r.status}`);
        return r.json() as Promise<ProvinceGeoJSON>;
      })
      .then((geojson) => {
        if (cancelled) return;
        const feature = geojson.features[0];
        if (feature) setGeoFeature(ensureCCW(feature));
        else setGeoError(true);
      })
      .catch(() => {
        if (!cancelled) setGeoError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // GeoJSON itself broken: silently omit the map section (don't crash the page)
  if (geoError) return null;

  const hospitalList = hospitals ?? [];

  // While the ~7KB static GeoJSON loads we render the section *shell* — header +
  // a reserved-height placeholder card — rather than nothing. Returning null and
  // then popping the whole ~480px section in a frame later shifts the grid down
  // after mount: a latent layout shift (CLS) on every load, and — because it
  // grows *above* the viewport — it trips browser scroll anchoring on a
  // back-navigation restore, shoving the restored position hundreds of px off
  // (issue #102). Reserving the footprint keeps the overview's height stable, so
  // the silhouette simply fades into its already-allocated box.
  return (
    <section className="hospital-map-section reveal" style={{ animationDelay: "0.10s" }}>
      <header className="hospital-map-section__head">
        <p className="hospital-grid__eyebrow">{STRINGS.mapEyebrow}</p>
        <h2 className="hospital-map-section__h2 serif">
          {STRINGS.mapH2Start}
          <em className="hospital-map-section__emphasis">{STRINGS.mapH2Emphasis}</em>
          {STRINGS.mapH2End}
        </h2>
        <p className="hospital-map-section__lede">{STRINGS.mapLede}</p>
      </header>
      {geoFeature ? (
        <HospitalMapCard
          feature={geoFeature}
          hospitals={hospitalList}
          isPending={isPending}
          isError={isError}
          onRetry={onRetry}
        />
      ) : (
        // Reserve exactly the loaded card's footprint: the inner block matches
        // the MAP_HEIGHT the ParentSize child fills, so the card doesn't grow
        // (and shift the grid) when the silhouette mounts.
        <div className="map-card" aria-hidden="true">
          <div style={{ height: MAP_HEIGHT }} />
        </div>
      )}
    </section>
  );
}
